import React, { useState, useEffect, useCallback } from 'react';
import { axiosInstance } from '../App';
import { toast } from 'sonner';
import {
  MapPin, Calendar, Clock, Building2, User, Phone, Mail, Globe,
  Plus, Trash2, X, FileText, Upload, Image, Eye, RefreshCw,
  ChevronDown, ChevronUp, Edit2, Navigation, Users, Briefcase
} from 'lucide-react';
// Note: Edit2 is used for the Edit button on visits, keep it imported

const PROJECT_STATUS_OPTIONS = [
  { value: 'new', label: 'New', color: 'bg-blue-100 text-blue-700' },
  { value: 'pricing', label: 'Pricing', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'followup', label: 'Follow-up', color: 'bg-purple-100 text-purple-700' },
  { value: 'awarded', label: 'Awarded', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  { value: 'executed', label: 'Executed', color: 'bg-gray-100 text-gray-700' }
];

const MATERIALS_LIST = [
  { key: 'fire_stop_sealant', label: 'Fire Stop Sealant' },
  { key: 'expansion_joint_cover', label: 'Expansion Joint Cover' },
  { key: 'raised_floor_system', label: 'Raised Floor System' },
  { key: 'rubber_protection', label: 'Rubber Protection' },
  { key: 'aluminum_floor_mat', label: 'Aluminum Floor Mat' },
  { key: 'pvc_handrail', label: 'PVC Handrail' },
  { key: 'phenolic_toilet_partition', label: 'Phenolic Toilet Partition' },
  { key: 'stair_nosing', label: 'Stair Nosing' },
  { key: 'steel_doors', label: 'Steel Doors' }
];

const FILE_TYPES = [
  { key: 'boq', label: 'BOQ Files', dbKey: 'boq_files', icon: FileText, color: 'blue' },
  { key: 'drawing', label: 'Drawings', dbKey: 'drawing_files', icon: FileText, color: 'purple' },
  { key: 'quotation', label: 'Quotations', dbKey: 'quotation_files', icon: FileText, color: 'green' },
  { key: 'order', label: 'Orders', dbKey: 'order_files', icon: FileText, color: 'orange' },
  { key: 'photo', label: 'Site Photos', dbKey: 'site_photos', icon: Image, color: 'pink' }
];

const VisitForm = ({ user }) => {
  const [visits, setVisits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVisit, setEditingVisit] = useState(null);
  const [expandedVisit, setExpandedVisit] = useState(null);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [gettingLocation, setGettingLocation] = useState(false);
  const [activeTab, setActiveTab] = useState('info');

  const initialFormState = {
    project_name: '',
    project_status: 'new',
    company_name: '',
    consultant_name: '',
    company_location: null,
    project_contacts: [{ name: '', job_title: '', mobile: '', email: '' }],
    company_contacts: [{ name: '', job_title: '', mobile: '', email: '' }],
    materials_offered: {
      fire_stop_sealant: false,
      expansion_joint_cover: false,
      raised_floor_system: false,
      rubber_protection: false,
      aluminum_floor_mat: false,
      pvc_handrail: false,
      phenolic_toilet_partition: false,
      stair_nosing: false,
      steel_doors: false,
      other: false,
      other_name: ''
    },
    location: null,
    notes: ''
  };

  const [formData, setFormData] = useState(initialFormState);

  const fetchVisits = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/visits');
      setVisits(response.data);
    } catch (error) {
      toast.error('Failed to load visits');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchVisits();
  }, [fetchVisits]);

  const getCurrentLocation = (type = 'project') => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    const key = type === 'project' ? 'location' : 'company_location';
    const label = type === 'project' ? 'Project' : 'Company';
    
    setGettingLocation(type);
    
    const options = {
      enableHighAccuracy: true,
      timeout: 30000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const locationData = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          timestamp: new Date().toISOString()
        };
        
        setFormData(prev => ({
          ...prev,
          [key]: locationData
        }));
        toast.success(`${label} location captured!`);
        setGettingLocation(null);
      },
      (error) => {
        let errorMsg = 'Unable to get location';
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location access denied. Please allow location in browser settings.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location information unavailable.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out. Please try again.';
            break;
          default:
            errorMsg = error.message;
        }
        toast.error(errorMsg);
        setGettingLocation(null);
      },
      options
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.project_name.trim()) {
      toast.error('Project name is required');
      return;
    }

    try {
      if (editingVisit) {
        await axiosInstance.put(`/visits/${editingVisit.id}`, formData);
        toast.success('Visit updated successfully');
      } else {
        await axiosInstance.post('/visits', formData);
        toast.success('Visit created successfully');
      }
      setShowModal(false);
      setFormData(initialFormState);
      setEditingVisit(null);
      fetchVisits();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save visit');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this visit?')) return;
    
    try {
      await axiosInstance.delete(`/visits/${id}`);
      toast.success('Visit deleted');
      fetchVisits();
    } catch (error) {
      toast.error('Failed to delete visit');
    }
  };

  const handleEdit = (visit) => {
    setEditingVisit(visit);
    setFormData({
      project_name: visit.project_name || '',
      project_status: visit.project_status || 'new',
      company_name: visit.company_name || '',
      consultant_name: visit.consultant_name || '',
      company_location: visit.company_location || null,
      project_contacts: visit.project_contacts?.length > 0 
        ? visit.project_contacts 
        : [{ name: '', job_title: '', mobile: '', email: '' }],
      company_contacts: visit.company_contacts?.length > 0 
        ? visit.company_contacts 
        : [{ name: '', job_title: '', mobile: '', email: '' }],
      materials_offered: visit.materials_offered || initialFormState.materials_offered,
      location: visit.location,
      notes: visit.notes || ''
    });
    setActiveTab('info');
    setShowModal(true);
  };

  const handleFileUpload = async (visitId, fileType, files) => {
    try {
      setUploadingFile(`${visitId}-${fileType}`);
      
      for (const file of files) {
        const formData = new FormData();
        formData.append('file', file);
        
        await axiosInstance.post(`/visits/${visitId}/upload?file_type=${fileType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
      }
      
      toast.success(`${files.length} file(s) uploaded successfully`);
      fetchVisits();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploadingFile(null);
    }
  };

  const handleFileDelete = async (visitId, fileUrl, fileType) => {
    if (!window.confirm('Delete this file?')) return;
    
    try {
      await axiosInstance.delete(`/visits/${visitId}/file?file_url=${encodeURIComponent(fileUrl)}&file_type=${fileType}`);
      toast.success('File deleted');
      fetchVisits();
    } catch (error) {
      toast.error('Failed to delete file');
    }
  };

  const addContact = (type) => {
    const key = type === 'project' ? 'project_contacts' : 'company_contacts';
    setFormData(prev => ({
      ...prev,
      [key]: [...prev[key], { name: '', job_title: '', mobile: '', email: '' }]
    }));
  };

  const removeContact = (type, index) => {
    const key = type === 'project' ? 'project_contacts' : 'company_contacts';
    if (formData[key].length > 1) {
      setFormData(prev => ({
        ...prev,
        [key]: prev[key].filter((_, i) => i !== index)
      }));
    }
  };

  const updateContact = (type, index, field, value) => {
    const key = type === 'project' ? 'project_contacts' : 'company_contacts';
    setFormData(prev => ({
      ...prev,
      [key]: prev[key].map((contact, i) => 
        i === index ? { ...contact, [field]: value } : contact
      )
    }));
  };

  const getStatusBadge = (status) => {
    const option = PROJECT_STATUS_OPTIONS.find(o => o.value === status);
    return option ? (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${option.color}`}>
        {option.label}
      </span>
    ) : null;
  };

  const getFileUrl = (fileUrl) => {
    if (!fileUrl) return '';
    if (fileUrl.startsWith('/api/')) {
      return `${process.env.REACT_APP_BACKEND_URL}${fileUrl}`;
    }
    return `${process.env.REACT_APP_BACKEND_URL}/api${fileUrl}`;
  };

  const openNewVisitModal = () => {
    setEditingVisit(null);
    setFormData(initialFormState);
    setActiveTab('info');
    getCurrentLocation('project');
    setShowModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 pb-20 md:pb-0" data-testid="visit-form-page">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-800">
            Visit Form
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Track project visits and contacts
          </p>
        </div>
        <button
          onClick={openNewVisitModal}
          className="btn-primary flex items-center justify-center gap-2 w-full sm:w-auto py-3 md:py-2 text-base"
          data-testid="new-visit-btn"
        >
          <Plus className="w-5 h-5" />
          New Visit
        </button>
      </div>

      {/* Stats Cards - Mobile Grid */}
      <div className="grid grid-cols-3 md:grid-cols-6 gap-2 md:gap-4">
        {PROJECT_STATUS_OPTIONS.map(status => {
          const count = visits.filter(v => v.project_status === status.value).length;
          return (
            <div key={status.value} className={`${status.color} rounded-xl p-3 md:p-4 text-center`}>
              <p className="text-xl md:text-2xl font-bold">{count}</p>
              <p className="text-xs md:text-sm truncate">{status.label}</p>
            </div>
          );
        })}
      </div>

      {/* Visits List - Mobile Cards */}
      <div className="space-y-3">
        {visits.length === 0 ? (
          <div className="bg-white rounded-xl p-8 md:p-12 text-center text-gray-500">
            <MapPin className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg">No visits recorded yet</p>
            <p className="text-sm mt-2">Tap "New Visit" to add your first visit</p>
          </div>
        ) : (
          visits.map(visit => (
            <div key={visit.id} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              {/* Visit Header - Touch Friendly */}
              <div 
                className="p-4 cursor-pointer active:bg-gray-50 transition-colors"
                onClick={() => setExpandedVisit(expandedVisit === visit.id ? null : visit.id)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="p-2 md:p-3 bg-blue-100 rounded-xl shrink-0">
                      <MapPin className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-bold text-gray-800 truncate">{visit.project_name}</h3>
                        {getStatusBadge(visit.project_status)}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1 text-xs md:text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3 md:w-4 md:h-4" />
                          {visit.visit_date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3 md:w-4 md:h-4" />
                          {visit.visit_time?.substring(0, 5)}
                        </span>
                      </div>
                      {visit.company_name && (
                        <p className="text-xs md:text-sm text-gray-600 mt-1 flex items-center gap-1">
                          <Building2 className="w-3 h-3" />
                          {visit.company_name}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={(e) => { e.stopPropagation(); handleEdit(visit); }}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg active:bg-blue-100"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); handleDelete(visit.id); }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg active:bg-red-100"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                    {expandedVisit === visit.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>

              {/* Expanded Details - Mobile Optimized */}
              {expandedVisit === visit.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50 space-y-4">
                  {/* Project Location */}
                  {visit.location && (
                    <a
                      href={`https://www.google.com/maps?q=${visit.location.latitude},${visit.location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-xl active:bg-blue-100"
                    >
                      <Navigation className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="font-medium text-blue-800">Project Location - Open in Maps</p>
                        <p className="text-xs text-blue-600">Accuracy: {visit.location.accuracy?.toFixed(0)}m</p>
                      </div>
                    </a>
                  )}

                  {/* Company Location */}
                  {visit.company_location && (
                    <a
                      href={`https://www.google.com/maps?q=${visit.company_location.latitude},${visit.company_location.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 p-3 bg-purple-50 border border-purple-200 rounded-xl active:bg-purple-100"
                    >
                      <Building2 className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="font-medium text-purple-800">Company Location - Open in Maps</p>
                        <p className="text-xs text-purple-600">Accuracy: {visit.company_location.accuracy?.toFixed(0)}m</p>
                      </div>
                    </a>
                  )}

                  {/* Quick Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {visit.consultant_name && (
                      <div className="bg-white p-3 rounded-lg border">
                        <p className="text-xs text-gray-500 mb-1">Consultant</p>
                        <p className="font-medium">{visit.consultant_name}</p>
                      </div>
                    )}
                  </div>

                  {/* Materials */}
                  {visit.materials_offered && Object.values(visit.materials_offered).some(v => v === true) && (
                    <div className="bg-white p-3 rounded-lg border">
                      <p className="text-xs text-gray-500 mb-2">Materials Offered</p>
                      <div className="flex flex-wrap gap-1">
                        {MATERIALS_LIST.filter(m => visit.materials_offered[m.key]).map(m => (
                          <span key={m.key} className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                            {m.label}
                          </span>
                        ))}
                        {visit.materials_offered.other && visit.materials_offered.other_name && (
                          <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded-full text-xs">
                            {visit.materials_offered.other_name}
                          </span>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Contacts */}
                  {(visit.project_contacts?.some(c => c.name) || visit.company_contacts?.some(c => c.name)) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {visit.project_contacts?.filter(c => c.name).length > 0 && (
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                            <Users className="w-3 h-3" /> Project Contacts
                          </p>
                          {visit.project_contacts.filter(c => c.name).map((c, i) => (
                            <div key={i} className="py-2 border-b last:border-0">
                              <p className="font-medium text-sm">{c.name}</p>
                              {c.job_title && <p className="text-xs text-gray-500">{c.job_title}</p>}
                              <div className="flex gap-3 mt-1">
                                {c.mobile && (
                                  <a href={`tel:${c.mobile}`} className="text-blue-600 text-xs flex items-center gap-1">
                                    <Phone className="w-3 h-3" />{c.mobile}
                                  </a>
                                )}
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="text-blue-600 text-xs flex items-center gap-1">
                                    <Mail className="w-3 h-3" />Email
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {visit.company_contacts?.filter(c => c.name).length > 0 && (
                        <div className="bg-white p-3 rounded-lg border">
                          <p className="text-xs text-gray-500 mb-2 flex items-center gap-1">
                            <Building2 className="w-3 h-3" /> Company Contacts
                          </p>
                          {visit.company_contacts.filter(c => c.name).map((c, i) => (
                            <div key={i} className="py-2 border-b last:border-0">
                              <p className="font-medium text-sm">{c.name}</p>
                              {c.job_title && <p className="text-xs text-gray-500">{c.job_title}</p>}
                              <div className="flex gap-3 mt-1">
                                {c.mobile && (
                                  <a href={`tel:${c.mobile}`} className="text-blue-600 text-xs flex items-center gap-1">
                                    <Phone className="w-3 h-3" />{c.mobile}
                                  </a>
                                )}
                                {c.email && (
                                  <a href={`mailto:${c.email}`} className="text-blue-600 text-xs flex items-center gap-1">
                                    <Mail className="w-3 h-3" />Email
                                  </a>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Files Section - Mobile Optimized */}
                  <div className="bg-white p-3 rounded-lg border">
                    <p className="text-xs text-gray-500 mb-3">Attached Files</p>
                    <div className="space-y-3">
                      {FILE_TYPES.map(fileType => {
                        const files = visit[fileType.dbKey] || [];
                        return (
                          <div key={fileType.key} className="border rounded-lg p-3">
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-medium text-sm flex items-center gap-2">
                                <fileType.icon className="w-4 h-4" />
                                {fileType.label}
                                <span className="bg-gray-200 px-2 py-0.5 rounded-full text-xs">{files.length}</span>
                              </span>
                              <label className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm cursor-pointer active:bg-blue-700 flex items-center gap-1">
                                <input
                                  type="file"
                                  className="hidden"
                                  onChange={(e) => e.target.files?.length && handleFileUpload(visit.id, fileType.key, Array.from(e.target.files))}
                                  disabled={uploadingFile === `${visit.id}-${fileType.key}`}
                                  multiple
                                />
                                {uploadingFile === `${visit.id}-${fileType.key}` ? (
                                  <RefreshCw className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Upload className="w-4 h-4" />
                                )}
                                Upload
                              </label>
                            </div>
                            {files.length > 0 && (
                              <div className="space-y-1 max-h-32 overflow-y-auto">
                                {files.map((file, i) => (
                                  <div key={i} className="flex items-center justify-between bg-gray-50 p-2 rounded text-sm">
                                    <a
                                      href={getFileUrl(file)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="flex items-center gap-2 text-blue-600 truncate flex-1 min-w-0"
                                    >
                                      <Eye className="w-4 h-4 shrink-0" />
                                      <span className="truncate">{file.split('/').pop()}</span>
                                    </a>
                                    <button
                                      onClick={() => handleFileDelete(visit.id, file, fileType.key)}
                                      className="p-1.5 text-red-500 hover:bg-red-100 rounded shrink-0 ml-2"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Modal - Full Screen on Mobile */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-end md:items-center justify-center" onClick={() => setShowModal(false)}>
          <div 
            className="bg-white w-full md:max-w-2xl md:rounded-2xl md:mx-4 rounded-t-2xl max-h-[95vh] md:max-h-[90vh] overflow-hidden flex flex-col" 
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="sticky top-0 bg-white z-10 px-4 py-3 border-b flex items-center justify-between">
              <h3 className="text-lg font-bold">
                {editingVisit ? 'Edit Visit' : 'New Visit'}
              </h3>
              <button onClick={() => setShowModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Tab Navigation */}
            <div className="flex border-b bg-gray-50 px-2">
              {[
                { id: 'info', label: 'Info', icon: Briefcase },
                { id: 'contacts', label: 'Contacts', icon: Users },
                { id: 'materials', label: 'Materials', icon: FileText }
              ].map(tab => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-3 px-2 text-sm font-medium flex items-center justify-center gap-1.5 border-b-2 transition-colors ${
                    activeTab === tab.id 
                      ? 'border-blue-600 text-blue-600 bg-white' 
                      : 'border-transparent text-gray-500'
                  }`}
                >
                  <tab.icon className="w-4 h-4" />
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Modal Content */}
            <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto">
              <div className="p-4 space-y-4">
                {/* Project Location Banner */}
                <div className="bg-blue-50 p-3 rounded-xl border border-blue-200">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Project Location (GPS)</p>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="flex items-center gap-1 text-blue-700">
                        <Calendar className="w-4 h-4" />
                        {new Date().toLocaleDateString()}
                      </span>
                      <span className="flex items-center gap-1 text-blue-700">
                        <Clock className="w-4 h-4" />
                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </span>
                      {formData.location && (
                        <span className="flex items-center gap-1 text-green-700 font-medium">
                          <MapPin className="w-4 h-4" />
                          ✓ Captured
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => getCurrentLocation('project')}
                      disabled={gettingLocation === 'project'}
                      className="flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm active:bg-blue-700 font-medium"
                    >
                      {gettingLocation === 'project' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4" />
                      )}
                      Capture GPS
                    </button>
                  </div>
                  {formData.location && (
                    <p className="text-xs text-green-600 mt-2">
                      📍 {formData.location.latitude.toFixed(6)}, {formData.location.longitude.toFixed(6)} 
                      (±{formData.location.accuracy?.toFixed(0)}m)
                    </p>
                  )}
                </div>

                {/* Company Location Banner */}
                <div className="bg-purple-50 p-3 rounded-xl border border-purple-200">
                  <p className="text-xs text-gray-600 mb-2 font-medium">Company Location (GPS)</p>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2 text-sm">
                      {formData.company_location ? (
                        <span className="flex items-center gap-1 text-green-700 font-medium">
                          <Building2 className="w-4 h-4" />
                          ✓ Captured
                        </span>
                      ) : (
                        <span className="text-gray-500">Not captured yet</span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => getCurrentLocation('company')}
                      disabled={gettingLocation === 'company'}
                      className="flex items-center gap-1.5 px-4 py-2 bg-purple-600 text-white rounded-lg text-sm active:bg-purple-700 font-medium"
                    >
                      {gettingLocation === 'company' ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Navigation className="w-4 h-4" />
                      )}
                      Capture GPS
                    </button>
                  </div>
                  {formData.company_location && (
                    <p className="text-xs text-purple-600 mt-2">
                      📍 {formData.company_location.latitude.toFixed(6)}, {formData.company_location.longitude.toFixed(6)} 
                      (±{formData.company_location.accuracy?.toFixed(0)}m)
                    </p>
                  )}
                </div>

                {/* Tab: Info */}
                {activeTab === 'info' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Project Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        value={formData.project_name}
                        onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                        className="w-full px-4 py-3 border rounded-xl text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Enter project name"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Project Status</label>
                      <select
                        value={formData.project_status}
                        onChange={(e) => setFormData({ ...formData, project_status: e.target.value })}
                        className="w-full px-4 py-3 border rounded-xl text-base focus:ring-2 focus:ring-blue-500"
                      >
                        {PROJECT_STATUS_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Company Name</label>
                      <input
                        type="text"
                        value={formData.company_name}
                        onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                        className="w-full px-4 py-3 border rounded-xl text-base"
                        placeholder="Company name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Consultant Name</label>
                      <input
                        type="text"
                        value={formData.consultant_name}
                        onChange={(e) => setFormData({ ...formData, consultant_name: e.target.value })}
                        className="w-full px-4 py-3 border rounded-xl text-base"
                        placeholder="Consultant name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={formData.notes}
                        onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                        className="w-full px-4 py-3 border rounded-xl text-base resize-none"
                        rows={3}
                        placeholder="Additional notes..."
                      />
                    </div>
                  </div>
                )}

                {/* Tab: Contacts */}
                {activeTab === 'contacts' && (
                  <div className="space-y-6">
                    {/* Project Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Users className="w-4 h-4" /> Project Contacts
                        </h4>
                        <button
                          type="button"
                          onClick={() => addContact('project')}
                          className="text-blue-600 text-sm flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" /> Add
                        </button>
                      </div>
                      {formData.project_contacts.map((contact, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-xl mb-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">Contact #{index + 1}</span>
                            {formData.project_contacts.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContact('project', index)}
                                className="text-red-500 p-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Name"
                            value={contact.name}
                            onChange={(e) => updateContact('project', index, 'name', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="text"
                            placeholder="Job Title"
                            value={contact.job_title}
                            onChange={(e) => updateContact('project', index, 'job_title', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="tel"
                            placeholder="Mobile Number"
                            value={contact.mobile}
                            onChange={(e) => updateContact('project', index, 'mobile', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={contact.email}
                            onChange={(e) => updateContact('project', index, 'email', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                        </div>
                      ))}
                    </div>

                    {/* Company Contacts */}
                    <div>
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="font-semibold flex items-center gap-2">
                          <Building2 className="w-4 h-4" /> Company Contacts
                        </h4>
                        <button
                          type="button"
                          onClick={() => addContact('company')}
                          className="text-blue-600 text-sm flex items-center gap-1"
                        >
                          <Plus className="w-4 h-4" /> Add
                        </button>
                      </div>
                      {formData.company_contacts.map((contact, index) => (
                        <div key={index} className="bg-gray-50 p-3 rounded-xl mb-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-xs text-gray-500">Contact #{index + 1}</span>
                            {formData.company_contacts.length > 1 && (
                              <button
                                type="button"
                                onClick={() => removeContact('company', index)}
                                className="text-red-500 p-1"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <input
                            type="text"
                            placeholder="Name"
                            value={contact.name}
                            onChange={(e) => updateContact('company', index, 'name', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="text"
                            placeholder="Job Title"
                            value={contact.job_title}
                            onChange={(e) => updateContact('company', index, 'job_title', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="tel"
                            placeholder="Mobile Number"
                            value={contact.mobile}
                            onChange={(e) => updateContact('company', index, 'mobile', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                          <input
                            type="email"
                            placeholder="Email"
                            value={contact.email}
                            onChange={(e) => updateContact('company', index, 'email', e.target.value)}
                            className="w-full px-3 py-2.5 border rounded-lg text-base"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Tab: Materials */}
                {activeTab === 'materials' && (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-500 mb-2">Select materials offered to the client:</p>
                    {MATERIALS_LIST.map(material => (
                      <label 
                        key={material.key} 
                        className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer active:bg-gray-50 transition-colors ${
                          formData.materials_offered[material.key] ? 'bg-green-50 border-green-300' : 'bg-white'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.materials_offered[material.key]}
                          onChange={(e) => setFormData({
                            ...formData,
                            materials_offered: {
                              ...formData.materials_offered,
                              [material.key]: e.target.checked
                            }
                          })}
                          className="w-5 h-5 rounded text-green-600"
                        />
                        <span className="text-base">{material.label}</span>
                      </label>
                    ))}
                    <label 
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer active:bg-gray-50 ${
                        formData.materials_offered.other ? 'bg-orange-50 border-orange-300' : 'bg-white'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={formData.materials_offered.other}
                        onChange={(e) => setFormData({
                          ...formData,
                          materials_offered: {
                            ...formData.materials_offered,
                            other: e.target.checked
                          }
                        })}
                        className="w-5 h-5 rounded text-orange-600"
                      />
                      <span className="text-base">Other</span>
                    </label>
                    {formData.materials_offered.other && (
                      <input
                        type="text"
                        placeholder="Enter other material name"
                        value={formData.materials_offered.other_name}
                        onChange={(e) => setFormData({
                          ...formData,
                          materials_offered: {
                            ...formData.materials_offered,
                            other_name: e.target.value
                          }
                        })}
                        className="w-full px-4 py-3 border rounded-xl text-base mt-2"
                      />
                    )}
                  </div>
                )}
              </div>

              {/* Submit Buttons - Sticky Footer */}
              <div className="sticky bottom-0 bg-white border-t p-4 flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 py-3 px-4 border border-gray-300 rounded-xl font-medium text-gray-700 active:bg-gray-100"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  className="flex-1 py-3 px-4 bg-blue-600 text-white rounded-xl font-medium active:bg-blue-700"
                >
                  {editingVisit ? 'Save Changes' : 'Create Visit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisitForm;
